import React, { useState } from 'react';
import { Slider } from './ui/slider';
import Reveal from './shared/Reveal';
import SectionHeader from './shared/SectionHeader';

// Defined outside LoanCalculator on purpose: a component declared inside another is
// re-created on every render, which remounts the slider and breaks dragging.
const SliderRow = ({ label, display, children, minLabel, maxLabel }) => (
  <div className="mb-8">
    <div className="flex justify-between items-baseline mb-3">
      <label className="text-sm font-semibold text-navy-800">{label}</label>
      <span className="font-display text-lg font-bold text-brand-600 tabular-nums">{display}</span>
    </div>
    {children}
    <div className="flex justify-between text-[11px] font-medium text-slate-400 mt-2">
      <span>{minLabel}</span>
      <span>{maxLabel}</span>
    </div>
  </div>
);

const LoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [interestRate, setInterestRate] = useState(10);
  const [tenure, setTenure] = useState(10);

  const calculateEMI = () => {
    const P = loanAmount;
    const r = interestRate / 12 / 100;
    const n = tenure * 12;
    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return Math.round(emi);
  };

  const monthlyEMI = calculateEMI();
  const totalPayment = monthlyEMI * tenure * 12;
  const totalInterest = totalPayment - loanAmount;
  const principalShare = Math.round((loanAmount / totalPayment) * 100);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);

  return (
    <section id="loan-calculator" className="py-20 lg:py-28 bg-white">
      <div className="container-x">
        <SectionHeader
          eyebrow="Plan your finances"
          title={<>Education loan <span className="text-brand-500">calculator</span></>}
          subtitle="Estimate your monthly EMI and total cost in seconds — then let us help you get the best rate."
        />

        <Reveal>
          <div className="rounded-3xl border border-navy-100 shadow-card overflow-hidden bg-white">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Inputs */}
              <div className="p-8 sm:p-10 lg:p-12 bg-navy-50/60">
                <h3 className="font-display text-xl font-bold text-navy-900 mb-8">Loan details</h3>

                <SliderRow
                  label="Loan Amount"
                  display={formatCurrency(loanAmount)}
                  minLabel="₹1,00,000"
                  maxLabel="₹1,00,00,000"
                >
                  <Slider
                    value={[loanAmount]}
                    onValueChange={(value) => setLoanAmount(value[0])}
                    min={100000}
                    max={10000000}
                    step={50000}
                  />
                </SliderRow>

                <SliderRow
                  label="Interest Rate (Annual)"
                  display={`${interestRate}%`}
                  minLabel="8%"
                  maxLabel="13%"
                >
                  <Slider
                    value={[interestRate]}
                    onValueChange={(value) => setInterestRate(value[0])}
                    min={8}
                    max={13}
                    step={0.5}
                  />
                </SliderRow>

                <SliderRow
                  label="Loan Tenure"
                  display={`${tenure} years`}
                  minLabel="1 year"
                  maxLabel="25 years"
                >
                  <Slider
                    value={[tenure]}
                    onValueChange={(value) => setTenure(value[0])}
                    min={1}
                    max={25}
                    step={1}
                  />
                </SliderRow>

                <a
                  href="https://wa.me/919059333987?text=Hi%2C%20I%20would%20like%20help%20with%20an%20education%20loan."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full mt-2"
                >
                  Apply for This Loan
                </a>
              </div>

              {/* Summary */}
              <div className="p-8 sm:p-10 lg:p-12">
                <h3 className="font-display text-xl font-bold text-navy-900 mb-8">Your summary</h3>

                {/* EMI hero card */}
                <div className="rounded-2xl bg-navy-900 text-white p-7 mb-8 relative overflow-hidden">
                  <div className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-brand-500/25 blur-2xl"></div>
                  <p className="text-sm text-navy-100/70 font-medium relative">Monthly EMI</p>
                  <p className="font-display text-4xl lg:text-[2.75rem] font-extrabold mt-1 relative tabular-nums">
                    {formatCurrency(monthlyEMI)}
                  </p>
                </div>

                <div className="flex items-center gap-7">
                  {/* Donut: principal vs interest */}
                  <div
                    className="w-28 h-28 rounded-full shrink-0 relative"
                    style={{
                      background: `conic-gradient(#13233d 0% ${principalShare}%, #f26b1d ${principalShare}% 100%)`
                    }}
                    role="img"
                    aria-label={`Principal ${principalShare}%, interest ${100 - principalShare}%`}
                  >
                    <div className="absolute inset-3 rounded-full bg-white flex flex-col items-center justify-center">
                      <span className="font-display font-bold text-navy-900 text-lg leading-none">{principalShare}%</span>
                      <span className="text-[10px] font-semibold text-slate-400 mt-0.5">principal</span>
                    </div>
                  </div>

                  <div className="space-y-3.5 flex-1">
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2 text-sm text-slate-500">
                        <span className="w-2.5 h-2.5 rounded-full bg-navy-900"></span>
                        Principal
                      </span>
                      <span className="font-semibold text-navy-900 text-sm tabular-nums">{formatCurrency(loanAmount)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2 text-sm text-slate-500">
                        <span className="w-2.5 h-2.5 rounded-full bg-brand-500"></span>
                        Interest
                      </span>
                      <span className="font-semibold text-brand-600 text-sm tabular-nums">{formatCurrency(totalInterest)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4 pt-3 border-t border-navy-100">
                      <span className="text-sm font-bold text-navy-900">Total payable</span>
                      <span className="font-display font-bold text-navy-900 tabular-nums">{formatCurrency(totalPayment)}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 rounded-2xl bg-brand-50 border border-brand-100 p-6">
                  <h4 className="font-display font-bold text-navy-900">Ready to apply?</h4>
                  <p className="text-sm text-slate-500 mt-1 mb-4">
                    Get pre-approved in minutes — we partner with leading lenders for education loans.
                  </p>
                  <a
                    href="https://wa.me/919059333987?text=Hi%2C%20I%20would%20like%20to%20start%20a%20loan%20application."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline-navy w-full"
                  >
                    Start Application
                  </a>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default LoanCalculator;
